import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Stack,
  Typography,
  Unstable_Grid2 as Grid
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import DashboardHero from 'components/dashboard/DashboardHero';
import useOrganizationBranding from 'hooks/useOrganizationBranding';
import PlanSplitCard from 'components/plan/PlanSplitCard';
import WellbeingCheckinCard from 'components/wellbeing/WellbeingCheckinCard';
import AppBarContext from 'context/AppBarContext';

const ALL_PLANS_QUERY = gql`
  query AllPlansLimited {
    allPlans {
      id
      name
      type
      platform
      image_url
      price_usd
      price_cop
      description
      sessions
      is_subscribable
      active_discount_percent
      org_benefit {
        discount_percent
        base_price
        discounted_price
        currency
        organization_name
      }
      session_types {
        id
        name
      }
    }
  }
`;

// Active Plus SIEMPRE primero en los acordeones.
const PLATFORM_ORDER = ['active_plus', 'otrix_app', 'otrix_play', 'otrix_gym'];

function LimitedDashboard() {
  const { t } = useTranslation(['routes', 'common', 'options']);
  // Marca de la empresa del usuario. Sin empresa (o en la organización por defecto) devuelve
  // nulos y el hero cae al degradado azulado de siempre.
  const branding = useOrganizationBranding();
  const { setAppBarButton, setAppBarTitle } = useContext(AppBarContext);
  const firstName = useSelector((state) => state.auth.user?.first_name);
  const userPlatform = useSelector((state) => state.auth.user?.platform) || 'otrix_app';

  const [openPlatform, setOpenPlatform] = useState(userPlatform);

  useEffect(() => {
    setAppBarButton(null);
    setAppBarTitle(t('home', { ns: 'routes' }));
  }, [setAppBarButton, setAppBarTitle, t]);

  const { data, loading } = useQuery(ALL_PLANS_QUERY, { fetchPolicy: 'network-only' });
  const allPlans = data?.allPlans || [];

  const platformLabels = {
    otrix_app: t('platform.otrixApp', { ns: 'options', defaultValue: 'Otrix Personalizado App' }),
    active_plus: t('platform.activePlus', { ns: 'options', defaultValue: 'Otrix Active Plus' }),
    otrix_play: t('platform.otrixPlay', { ns: 'options', defaultValue: 'OTRIX Play' }),
    otrix_gym: t('platform.otrixGym', { ns: 'options', defaultValue: 'OTRIX Gym' })
  };

  const plansByPlatform = PLATFORM_ORDER.reduce((acc, p) => {
    const filtered = allPlans.filter((plan) => (plan.platform || 'otrix_app') === p);
    if (filtered.length > 0) acc[p] = filtered;
    return acc;
  }, {});

  const toggle = (p) => setOpenPlatform((prev) => (prev === p ? null : p));

  return (
    <>
      <SEOPageTitle title={t('home', { ns: 'routes' })} />
      <Stack spacing={{ xs: 3, lg: 4 }}>
        <DashboardHero
          backgroundImage={branding.cover}
          logoImage={branding.logo}
          eyebrow={t('home', { ns: 'routes' })}
          greeting={t('dashboard.welcomeHeroTitle', { ns: 'common', defaultValue: 'Bienvenido a Otrix,' })}
          highlight={firstName}
          subtitle={t('dashboard.plansSubtitle', {
            ns: 'common',
            defaultValue: 'Adquiere un plan para acceder a todas sus funcionalidades.'
          })}
        />

        {/* Bienestar Corporativo aplica sin importar el plan: los miembros de organización
            sin membresía viven aquí y también responden su check-in semanal. */}
        <WellbeingCheckinCard />

        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress color="secondary" />
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            {Object.entries(plansByPlatform).map(([p, plans]) => {
              const isOpen = openPlatform === p;
              return (
                <Box key={p}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    onClick={() => toggle(p)}
                    sx={{
                      px: 0.5,
                      py: 1,
                      mb: 1.5,
                      cursor: 'pointer',
                      borderBottom: '1px solid',
                      borderColor: 'hairline.main'
                    }}>
                    <Stack direction="row" spacing={1.25} alignItems="baseline">
                      <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em' }}>
                        {platformLabels[p]}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                        {plans.length}{' '}
                        {plans.length === 1
                          ? t('dashboard.plan', { ns: 'common', defaultValue: 'plan' })
                          : t('dashboard.plans', { ns: 'common', defaultValue: 'planes' })}
                      </Typography>
                    </Stack>
                    <IconButton
                      size="small"
                      aria-label={isOpen ? 'contraer' : 'expandir'}
                      sx={{
                        color: isOpen ? 'gold.main' : 'text.secondary',
                        transform: isOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 220ms cubic-bezier(0.32,0.72,0,1)'
                      }}>
                      <ExpandMoreIcon />
                    </IconButton>
                  </Stack>

                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <Box sx={{ pb: { xs: 1, lg: 2 } }}>
                      <Grid container spacing={{ xs: 2, lg: 3 }} alignItems="stretch">
                        {plans.map((plan) => (
                          <Grid key={plan.id} xs={12} md={6}>
                            <PlanSplitCard plan={plan} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </>
  );
}

export default LimitedDashboard;
